import { useState } from "react";
import Head from "next/head";
import Image from "next/image";

import {
  BadgeCheck,
  ComputerIcon,
  ExternalLink,
  Facebook,
  Instagram,
  Linkedin,
  Star,
  Twitter,
  Youtube,
} from "lucide-react";

import Container from "@/components/Container";
import useRouterQuery from "@/components/hooks/useRouterQuery";
import BBBIcon from "@/components/icons/BBB";
import NextDoorIcon from "@/components/icons/NextDoor";
import PinterestIcon from "@/components/icons/Pinterest";
import TikTokIcon from "@/components/icons/TikTok";
import YelpIcon from "@/components/icons/Yelp";
import ContactAddButton from "@/components/linktree/ContactAddButton";
import InactiveWebsitePrompt from "@/components/linktree/InactiveWebsitePrompt";
import PhoneCallButton from "@/components/linktree/PhoneCallButton";
import { MIN_REVIEWS } from "@/lib/constants";
import prisma from "@/lib/db";
import { constructImageUrl, getBaseUrl, getHeightAndWidth, logError } from "@/lib/utils";
import ErrorPage from "@/pages/error";
import { WebsiteWithConversionsLocationsImages } from "@/types/prisma";

export async function getServerSideProps({ params }: { params: { subdomain?: string } }) {
  const HOME_REDIRECT = { redirect: { destination: "/", permanent: false } } as const;

  if (!params?.subdomain) {
    return HOME_REDIRECT;
  }

  const subdomain = params.subdomain.toLowerCase();

  try {
    const website = await prisma.website.findUnique({
      where: { subdomain },
      select: {
        subdomain: true,
        companyName: true,
        industry: true,
        active: true,
        logo: true,
        favicon: true,
        logoAspectRatio: true,
        googReviewCount: true,
        customDomain: true,
        // Social media fields
        instagramUrl: true,
        facebookUrl: true,
        twitterUrl: true,
        youtubeUrl: true,
        linkedinUrl: true,
        nextdoorUrl: true,
        pinterestUrl: true,
        bbbUrl: true,
        yelpUrl: true,
        tiktokUrl: true,
        // Related data
        locations: {
          select: {
            googlePlaceId: true,
            city: true,
            state: true,
            address: true,
            phoneNumber: true,
            email: true,
          },
        },
        conversions: {
          select: {
            id: true,
            type: true,
            value: true,
            priority: true,
            ctaText: true,
          },
        },
        reviews: {
          select: {
            id: true,
          },
        },
        images: true,
      },
    });

    if (!website) {
      return HOME_REDIRECT;
    }

    return {
      props: {
        initialWebsite: JSON.parse(JSON.stringify(website)), // Serialize dates
      },
    };
  } catch (error) {
    logError("Error in getServerSideProps", error);

    return HOME_REDIRECT;
  }
}

export default function Company({
  initialWebsite,
  error: initialError,
}: {
  initialWebsite: WebsiteWithConversionsLocationsImages;
  error: string;
}) {
  const subdomain = useRouterQuery("subdomain");
  const [website] = useState<WebsiteWithConversionsLocationsImages>(initialWebsite);
  const [error] = useState(initialError);

  const reviewCount = website?.googReviewCount || 0;

  if (error || !website) {
    return <ErrorPage />;
  }

  const socialMediaInfo = {
    instagramUrl: { name: "Instagram", icon: Instagram },
    facebookUrl: { name: "Facebook", icon: Facebook },
    twitterUrl: { name: "Twitter", icon: Twitter },
    youtubeUrl: { name: "YouTube", icon: Youtube },
    linkedinUrl: { name: "LinkedIn", icon: Linkedin },
    nextdoorUrl: { name: "NextDoor", icon: NextDoorIcon },
    pinterestUrl: { name: "Pinterest", icon: PinterestIcon },
    yelpUrl: { name: "Yelp", icon: YelpIcon },
    tiktokUrl: { name: "TikTok", icon: TikTokIcon },
    bbbUrl: { name: "Better Business Bureau", icon: BBBIcon },
  };

  //Add a multiplier to increase the size
  const logoMultiplier = 1.15; //Increase size by 15%

  let { height, width } = getHeightAndWidth(website.logoAspectRatio);

  const socialsArray = Object.entries(website)
    .filter(([key, url]) => socialMediaInfo[key as keyof typeof socialMediaInfo] && url)
    .map(([key, url]) => ({
      name: socialMediaInfo[key as keyof typeof socialMediaInfo].name,
      href: typeof url === "string" ? (url.startsWith("http") ? url : `https://${url}`) : "",
      icon: socialMediaInfo[key as keyof typeof socialMediaInfo].icon,
    }));

  const conversionActions =
    website?.conversions?.sort((a: { priority: number }, b: { priority: number }) => a.priority - b.priority) || [];
  const googlePlaceId = website?.locations?.[0]?.googlePlaceId;
  const BGPhoto = website.images[0];

  const links = [
    {
      href: getBaseUrl(website.subdomain, website?.customDomain),
      icon: ComputerIcon,
      text: "Our website",
      iconClass: "text-ash-900",
      show: true,
    },
    {
      href: `https://search.google.com/local/reviews?placeid=${googlePlaceId}`,
      icon: Star,
      text: "See all Google reviews",
      iconClass: "text-honey-500 fill-current",
      // Only show if we have reviews and meet minimum threshold
      show: reviewCount > MIN_REVIEWS,
    },
    {
      href: `https://search.google.com/local/writereview?placeid=${googlePlaceId}`,
      icon: () => (
        <Image src="/google-g-logo.svg" width={20} height={20} alt="google logo" className="relative h-4 w-4" />
      ),
      text: "Leave us a review",
      show: !!googlePlaceId,
    },
  ];

  // Create a constant for the hover animation class
  const hoverAnimationClass =
    "hover:-translate-y-0.5 transform transition-all duration-200 ease-in-out active:translate-y-0.5";

  // Create meta description
  const metaDescription = `${website.companyName} - ${website.industry} ${
    website?.locations?.[0]?.address ? ` in ${website?.locations?.[0]?.city}, ${website?.locations?.[0]?.state}` : ""
  }. ${reviewCount > MIN_REVIEWS ? `Rated 5 stars with ${reviewCount} reviews.` : ""}`;

  return (
    <div className="bg-ash-100 min-h-screen text-ash-900 relative">
      <Head>
        <title>{`${website.companyName} | Our Links`}</title>
        <meta name="description" content={metaDescription} />

        {website.favicon && <link key="favicon" rel="icon" href={website.favicon} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${website.companyName} | Our Links`} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content={`https://api.screenshotone.com/take?access_key=cQWOH7FyQTatDw&url=https://rebolthq.com/company/${subdomain}&full_page=false&viewport_width=414&viewport_height=750&device_scale_factor=1&format=jpg&image_quality=80&cache=true&cache_ttl=304800`}
        />

        {/* Additional Meta Tags */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      {BGPhoto && (
        <div
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${constructImageUrl(BGPhoto?.providerId, BGPhoto?.source || "")})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-linear-to-b from-white/30 to-white/40" />
          <div className="absolute inset-0 backdrop-blur-3xl" />
        </div>
      )}

      <div className="relative z-10">
        <div className="fixed flex justify-center bottom-0 left-0 right-0 w-full p-5 md:flex-row flex-col gap-4 z-10">
          <ContactAddButton
            website={website}
            className={`md:hidden rounded-md shadow-sm px-2 py-2 whitespace-nowrap text-center text-base md:px-4 md:py-3 text-ash-100 ${hoverAnimationClass}`}
          />
          {conversionActions.map((action) => {
            if (action.type === "PHONE") {
              return (
                <PhoneCallButton
                  key={action.id}
                  conversion={action}
                  primaryClass={`font-semibold rounded-full outline-solid outline-ash-100 px-2 py-2 whitespace-nowrap text-center text-base md:px-4 md:py-3 text-ash-70 bg-white ${hoverAnimationClass}`}
                />
              );
            }
          })}
        </div>

        {website.active === false && <InactiveWebsitePrompt website={website} />}

        <Container className="max-w-3xl">
          <div className="flex flex-col gap-12 py-14 pb-36">
            <div className="flex flex-col gap-10">
              <div className="flex gap-8 items-center justify-center flex-col">
                <div className="flex flex-col gap-6 items-center">
                  {website?.logo && (
                    <div className="relative">
                      <img
                        src={website?.logo}
                        alt={website?.companyName || ""}
                        width={width * logoMultiplier}
                        height={height * logoMultiplier}
                        sizes="(max-width: 768px) 100vw, 33vw"
                        className="rounded-md"
                      />

                      <BadgeCheck className="absolute -bottom-3 -right-3 h-8 w-8 text-honey-800 bg-honey-100 rounded-full p-1" />
                    </div>
                  )}
                  <div className="flex flex-col gap-2 items-center text-center">
                    <div className="flex items-center gap-4">
                      <h1 className="text-4xl font-bold">{website.companyName}</h1>
                    </div>
                    <h3 className="text-ash-900/60">
                      {`${website.industry} ${website?.locations?.[0]?.address ? `in ${website?.locations?.[0]?.city}, ${website?.locations?.[0]?.state}` : ""}`}
                    </h3>
                    {reviewCount > MIN_REVIEWS && (
                      <div className="inline-flex gap-1 rounded-full text-honey-400">
                        <Star className="h-4 w-4 fill-current" />
                        <Star className="h-4 w-4 fill-current" />
                        <Star className="h-4 w-4 fill-current" />
                        <Star className="h-4 w-4 fill-current" />
                        <Star className="h-4 w-4 fill-current" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 items-center justify-center bg-white/30 rounded-full px-3 py-1">
                  {socialsArray.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center p-1 lg:p-4"
                    >
                      <item.icon aria-hidden="true" className="h-6 w-6 lg:h-6 lg:w-6" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-4 justify-center flex-col">
              {links.map(
                (link, index) =>
                  link.show && (
                    <div
                      key={link.text}
                      className="opacity-0 animate-fade-in-stagger"
                      style={{ animationDelay: `${index * 200}ms` }}
                    >
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        href={link.href}
                        className="w-full font-medium flex items-center justify-between gap-4 p-4 bg-ash-100/90 rounded-full outline-solid outline-ash-200 hover:-translate-y-0.5 transition-all duration-200 ease-in-out active:translate-y-0.5"
                      >
                        <div className="flex items-center gap-4">
                          <link.icon className={`h-4 w-4 ${link.iconClass || ""}`} />
                          {link.text}
                        </div>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                  )
              )}
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
